/**
 * Opening and closing animation for the buy box's <details> panels.
 *
 * WHY THIS IS JAVASCRIPT AND NOT CSS
 *
 * The CSS-only way to animate a <details> is to transition block-size on
 * ::details-content with interpolate-size: allow-keywords, which is what
 * assets/section-collapsible-content.css describes. It was tried here first
 * and no transition is ever created: getAnimations() on the panel reports
 * nothing running for block-size, and the panel jumps to full height in one
 * frame. That was checked against the real markup and against a bare <details>
 * built in the console, inside and outside the buy box's flex column, so it is
 * not this theme's markup - the transition simply does not start on this page.
 *
 * So the height is animated explicitly, from a measured value, which works
 * everywhere and can be verified: while it runs there is a real Animation
 * object on the body element.
 *
 * WHAT IT DOES
 * - Opening: the panel is opened immediately (so the content is in the
 *   accessibility tree and findable), then its body is animated 0 -> measured.
 * - Closing: the animation runs FIRST and `open` is removed at the end,
 *   because removing it up front destroys the thing being animated.
 *
 * WITHOUT JAVASCRIPT the panels are native <details> and toggle instantly,
 * which is the behaviour they had before this file existed.
 */
(function () {
  /* THE FAQ'S OWN NUMBERS, not a second set that merely looks similar.
     620/420 and this curve are read from --faq-open / --faq-close /
     --faq-ease in assets/section-collapsible-content.css. These panels and the
     FAQ's are the same control - a <details> with a <summary> - so two
     different speeds on one site reads as one of them being broken, and this
     was 420/300 against the FAQ's 620/420.

     Opening is slower than closing on purpose: arriving content wants to be
     watched, leaving content only needs to get out of the way. If the FAQ's
     timings change, change these with them. */
  var OPEN_MS = 620;
  var CLOSE_MS = 420;
  var EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';
  /* The FAQ's travel too. The height alone reads as a reflow rather than as
     motion; the body also drops into place behind its own summary, which is
     what makes it look like a panel opening. */
  var TRAVEL = '-12px';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function setup(details) {
    var summary = details.querySelector('summary');
    var body = details.querySelector('.product-accordion__body');
    if (!summary || !body) return;

    /* The animation in flight, if any. Kept so it can be cancelled rather
       than left running: an interrupted animation holds its first keyframe,
       and this one starts at height 0 and opacity 0, so a panel interrupted
       mid-open is a panel that has silently vanished. That is not a
       hypothetical - it happened during testing when the animation stopped
       advancing while the tab was not painting, and the panel stayed blank
       until the animation was cancelled by hand. */
    var current = null;

    function run(keyframes, duration, onDone) {
      if (current) current.cancel();
      current = body.animate(keyframes, { duration: duration, easing: EASING });
      /* Both events, not the finished promise: a cancelled animation rejects
         that promise, and a rejection nobody handles is an unhandled error
         AND leaves the panel in whatever state it was interrupted in. */
      current.onfinish = function () { current = null; if (onDone) onDone(); };
      current.oncancel = function () { current = null; };
      return current;
    }

    summary.addEventListener('click', function (event) {
      if (prefersReducedMotion()) return; /* native toggle, no animation */

      event.preventDefault();

      if (!details.open) {
        details.open = true;
        /* Measured after opening, because a closed panel has no height to
           measure - the browser gives its content no layout at all. */
        var target = body.scrollHeight;
        /* No fill, so nothing is left behind when it finishes: the panel goes
           back to its natural auto height and reflows correctly if the
           viewport changes or an image inside it loads late. */
        run([
          { height: '0px', opacity: 0, transform: 'translateY(' + TRAVEL + ')' },
          { height: target + 'px', opacity: 1, transform: 'translateY(0)' },
        ], OPEN_MS);
      } else {
        var from = body.scrollHeight;
        /* `open` is removed at the END, because removing it up front destroys
           the thing being animated. */
        run([
          { height: from + 'px', opacity: 1, transform: 'translateY(0)' },
          { height: '0px', opacity: 0, transform: 'translateY(' + TRAVEL + ')' },
        ], CLOSE_MS, function () { details.open = false; });
      }
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('.product-accordion').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Section rendering API: the buy box is re-rendered on variant change. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();

